import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useLazyCommand, useEvent } from '@soap/modules';
import { ArrowLeft, CheckSquare, GraduationCap } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { CurriculumHeader } from '../../components/CurriculumHeader';
import { Button } from '../../ui/button';
import { StudentSelector } from '../../components/StudentSelector';
import { useSelectedStudent } from '../../hooks/useSelectedStudent';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import { libraryYearDisplayName } from '../../components/LessonsPage/utils/curriculumUtils';
import { LYW_BRAND } from '../../constants/brandPalette';

// Backend returns subject-level counts
type SubjectRow = {
  e293_SubjectName?: string;
  e293_TopicsInCurriculum?: number | null;
  e293_TopicsWithCompletedAssessments?: number | null;
};

type CurriculumYearData = {
  e179_Number?: number | null;
  e179_Name?: string | null;
  e179_YearId?: string | null;
};

type CurriculumKeyStage = {
  e179_Years?: Array<CurriculumYearData>;
};

type CurriculumSubject = {
  e179_KeyStages?: Array<CurriculumKeyStage>;
};

type CurriculumStructureEvent = {
  e179_Subjects?: Array<CurriculumSubject>;
};

type CurriculumYearOption = {
  yearId: string;
  label: string;
};

const VIEW = { idle: 0, loading: 1, ready: 2, noStudent: 3, noData: 4 };

// Colors for pie chart segments
const COLORS = {
  withAssessments: LYW_BRAND.confirmAction,
  withoutAssessments: '#94a3b8', // slate-400
};

function parseYearN(label: string): number | null {
  const m = /^Year\s+(\d+)$/i.exec(label.trim());
  return m ? parseInt(m[1], 10) : null;
}

export function AssessmentCoverageReportPage() {
  const { selectedStudentId, students, setSelectedStudent, isLoading: isStudentLoading } = useSelectedStudent();
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [reportData, setReportData] = useState<SubjectRow[] | null>(null);
  const [viewState, setViewState] = useState<number>(VIEW.idle);
  const [curriculumYears, setCurriculumYears] = useState<CurriculumYearOption[]>([]);

  const [sendGetReport] = useLazyCommand('C293v1_GetAssessmentCoverageReportData');
  const [sendGetCurriculum] = useLazyCommand('C258v1_GetCurriculumStructure');

  useEvent({
    eventName: 'E293v1_GotAssessmentCoverageReportData',
    onEventReceived: (event: {
      e293_Subjects?: SubjectRow[];
      e293_LatestEducationSystemYearIdWithData?: string | null;
    }) => {
      const subjects = event?.e293_Subjects ?? [];
      setReportData(subjects);

      if (!selectedYear && event?.e293_LatestEducationSystemYearIdWithData && curriculumYears.length > 0) {
        const latest = String(event.e293_LatestEducationSystemYearIdWithData).trim().toLowerCase();
        const match = curriculumYears.find((y) => y.yearId.toLowerCase() === latest);
        if (match) {
          setSelectedYear(match.yearId);
          return;
        }
      }

      if (
        subjects.length === 0 &&
        event?.e293_LatestEducationSystemYearIdWithData &&
        curriculumYears.length > 0 &&
        selectedYear
      ) {
        const latest = String(event.e293_LatestEducationSystemYearIdWithData).trim().toLowerCase();
        const match = curriculumYears.find((y) => y.yearId.toLowerCase() === latest);
        if (match && selectedYear.toLowerCase() !== match.yearId.toLowerCase()) {
          setSelectedYear(match.yearId);
          return;
        }
      }

      setViewState(VIEW.ready);
    },
  });

  useEvent({
    eventName: 'E179v1_GotCurriculumStructure',
    onEventReceived: (event: CurriculumStructureEvent) => {
      if (event?.e179_Subjects) {
        const yearsSet = new Map<string, CurriculumYearOption>();

        event.e179_Subjects.forEach((subject) => {
          subject.e179_KeyStages?.forEach((keyStage) => {
            keyStage.e179_Years?.forEach((year) => {
              const yearId = year.e179_YearId != null ? String(year.e179_YearId).trim() : '';
              if (!yearId) return;
              const label =
                (year.e179_Name || '').trim() || libraryYearDisplayName(year);
              if (!label) return;
              if (!yearsSet.has(yearId)) {
                yearsSet.set(yearId, { yearId, label });
              }
            });
          });
        });

        const years = Array.from(yearsSet.values()).sort((a, b) => {
          const na = parseYearN(a.label);
          const nb = parseYearN(b.label);
          if (na != null && nb != null) return na - nb;
          if (na != null) return -1;
          if (nb != null) return 1;
          return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
        });

        setCurriculumYears(years);
      }
    },
  });

  useEffect(() => {
    sendGetCurriculum({ C258_CurriculumProviderKey: 'oak' });
  }, []);

  useEffect(() => {
    if (isStudentLoading) {
      setViewState(VIEW.loading);
      return;
    }
    if (!selectedStudentId) {
      setReportData(null);
      setViewState(VIEW.noStudent);
      return;
    }
    if (!selectedYear) {
      if (curriculumYears.length > 0) {
        const highestYear = curriculumYears.reduce((latest, current) => {
          const ln = parseYearN(latest.label);
          const cn = parseYearN(current.label);
          if (ln != null && cn != null) return cn > ln ? current : latest;
          if (cn != null) return current;
          if (ln != null) return latest;
          return current.label.localeCompare(latest.label, undefined, { sensitivity: 'base' }) > 0 ? current : latest;
        });
        setViewState(VIEW.loading);
        sendGetReport({
          C293_StudentId: selectedStudentId,
          C293_EducationSystemYearId: highestYear.yearId,
          C293_CurriculumProviderKey: 'oak',
        });
      }
      return;
    }
    setViewState(VIEW.loading);

    sendGetReport({
      C293_StudentId: selectedStudentId,
      C293_EducationSystemYearId: selectedYear,
      C293_CurriculumProviderKey: 'oak',
    });
  }, [selectedStudentId, selectedYear, isStudentLoading, curriculumYears, sendGetReport]);

  return (
    <div className="p-4 sm:p-6">
        <CurriculumHeader showTabs={false} title="Assessment Coverage" icon={CheckSquare} showStudentDropdown={false} />
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <Link to="/app/reports">
            <Button variant="outline" size="sm" className="gap-2">
              <ArrowLeft size={16} />
              Back to Reports
            </Button>
          </Link>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="report-student" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Student
              </Label>
              <StudentSelector
                students={students}
                selectedStudentId={selectedStudentId}
                onSelect={setSelectedStudent}
                showLabel={false}
                placeholder="Select a student"
                className="min-w-[180px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="report-year" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Year
              </Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger id="report-year" className="w-[180px]">
                  <SelectValue
                    placeholder="Select year"
                    label={
                      curriculumYears.find(
                        (y) => y.yearId.toLowerCase() === selectedYear.toLowerCase()
                      )?.label
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {curriculumYears.map((year) => (
                    <SelectItem key={year.yearId} value={year.yearId}>
                      {year.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {viewState === VIEW.noStudent && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <GraduationCap className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a student</h3>
            <p className="text-gray-600 mb-4">
              Choose a student and curriculum year from the dropdowns above to see assessment coverage by subject (topics in curriculum vs topics with completed assessments).
            </p>
            <p className="text-sm text-gray-500">Add students in Profiles if you do not have any yet.</p>
          </div>
        )}

        {viewState === VIEW.loading && (
          <div className="flex justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue" />
              <span className="text-gray-600">Loading…</span>
            </div>
          </div>
        )}

        {viewState === VIEW.ready && reportData && (
          <div className="space-y-8">
            {reportData.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                <CheckSquare className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No data for this year</h3>
                <p className="text-gray-600">
                  There are no topics in this student's curriculum for the selected year. Add topics in Plan first.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {reportData.map((subject) => {
                  const subjectName = subject.e293_SubjectName ?? 'Subject';
                  const topicsInCurriculum = Number(subject.e293_TopicsInCurriculum ?? 0);
                  const topicsWithAssessments = Number(subject.e293_TopicsWithCompletedAssessments ?? 0);
                  const topicsWithoutAssessments = topicsInCurriculum - topicsWithAssessments;

                  const chartData = [
                    {
                      name: 'With completed assessments',
                      value: topicsWithAssessments,
                    },
                    {
                      name: 'Without completed assessments',
                      value: topicsWithoutAssessments,
                    },
                  ];

                  return (
                    <div key={subjectName} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="p-4 space-y-4">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0">
                          <span className="font-semibold text-gray-900 text-lg">{subjectName}</span>
                          <span className="text-sm text-gray-500 flex flex-wrap items-center gap-3">
                            <span className="inline-flex items-center gap-1.5">
                              <span className="w-3 h-3 rounded bg-slate-400" /> Total topics: {topicsInCurriculum}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <span className="w-3 h-3 rounded bg-confirm-action" /> With assessments: {topicsWithAssessments}
                            </span>
                          </span>
                        </div>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {chartData.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={index === 0 ? COLORS.withAssessments : COLORS.withoutAssessments}
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    return (
                                      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                                        <p className="font-semibold mb-2">{payload[0].payload.name}</p>
                                        <p className="text-sm" style={{ color: payload[0].color }}>
                                          Topics: {payload[0].value}
                                        </p>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
  );
}
